import React, { ChangeEvent } from 'react';
import { connect } from 'react-redux';

import { Form, FormField } from 'components/form';
import Input from 'components/inputs/input/Input';

import InfluencerSettingsDocumentUpload from './influencer-settings-document-upload/InfluencerSettingsDocumentUpload';

import { LONG_STRING_MAX_LENGTH, isRequired, isValidAccountNumber, maxLength } from 'utils/validators';
import { __t, applyTranslations } from 'utils/language';

import './bank-details-settings.scss';

applyTranslations(require('./bank-details-settings.i18n.json'));

import {
  OrganizationSettingsForm,
  ProofDocumentFile,
  UploadFileResponse
} from 'types';

type StateProps = {
  isDocumentProofSectionEnabled: boolean
}

type OwnProps = {
  onTextInput: (event: ChangeEvent<HTMLInputElement>, type: string, shouldBeInUpperCase: boolean) => void,
  canEdit: boolean,
  values: {
    bankDetailsProof: UploadFileResponse | null,
    bankDetails: OrganizationSettingsForm['bankDetails']
  },
  children?: React.ReactNode,
  direction?: 'row' | 'column',
  labelType?: 'normal' | 'highlighted',
  onChange: (value: string | UploadFileResponse | null, name: string, type?: string) => void,
  onPreUpload: (
    value: ProofDocumentFile,
    name: string,
    type: string
  ) => void,
  onSubmit?: () => void
}

type Props = StateProps & OwnProps;

const MSG_KEY = 'bank_details_settings';

const BankDetailsSettingsForm = ({
  onSubmit,
  onTextInput,
  onPreUpload,
  onChange,
  canEdit,
  children,
  direction,
  labelType,
  isDocumentProofSectionEnabled,
  values
}: Props) => {
  const width = direction === 'column' ? 'responsive' : 'fixed';
  const { bankDetails, bankDetailsProof } = values;

  const document = bankDetailsProof;

  const handleFocus = (event) => event.target.select();

  const renderFields = () => (
    <React.Fragment>
      <div className="bank-details-settings__title">
        {__t(`${MSG_KEY}.bank_details_title`)}
      </div>
      <FormField
        direction={direction}
        label={__t(`${MSG_KEY}.account_name`)}
        labelType={labelType}
        labelSize="custom"
        fontSize="small"
        showRequiredMark
        validators={[
          isRequired(bankDetails.accountHolder)
        ]}
      >
        <Input
          type="text"
          name="accountHolder"
          width={width}
          value={bankDetails.accountHolder}
          disabled={!canEdit}
          onChange={(e) => onTextInput(e, 'bankDetails', false)}
          onFocus={handleFocus}
        />
      </FormField>
      <FormField
        direction={direction}
        label={__t(`${MSG_KEY}.account_number`)}
        labelType={labelType}
        labelSize="custom"
        fontSize="small"
        showRequiredMark
        validators={[
          isRequired(bankDetails.accountNumber),
          isValidAccountNumber(bankDetails.accountNumber)
        ]}
      >
        <Input
          type="text"
          name="accountNumber"
          width={width}
          value={bankDetails.accountNumber}
          disabled={!canEdit}
          onChange={(e) => onTextInput(e, 'bankDetails', true)}
          onFocus={handleFocus}
        />
      </FormField>

      <FormField
        direction={direction}
        label={__t(`${MSG_KEY}.swift`)}
        labelType={labelType}
        labelSize="custom"
        fontSize="small"
        showRequiredMark
        validators={[
          isRequired(bankDetails.swift),
          maxLength(bankDetails.swift, LONG_STRING_MAX_LENGTH)
        ]}
      >
        <Input
          type="text"
          name="swift"
          width={width}
          value={bankDetails.swift}
          disabled={!canEdit}
          onChange={(e) => onTextInput(e, 'bankDetails', true)}
          onFocus={handleFocus}
        />
      </FormField>
      {
        isDocumentProofSectionEnabled && (
          <FormField
            direction={canEdit ? 'column' : direction}
            className={`bank-details-settings__tax-document-container-${canEdit ? 'edit' : 'view'}`}
            label={canEdit ? '' : __t(`${MSG_KEY}.tax_registration_document.title`)}
            labelType={labelType}
            labelSize="custom"
            fontSize="small"
          >
            <InfluencerSettingsDocumentUpload
              title={__t(`${MSG_KEY}.tax_registration_document.title`)}
              info={__t(`${MSG_KEY}.tax_registration_document.info`)}
              uploadButtonLabel={__t(`${MSG_KEY}.tax_registration_document.submit_btn`)}
              document={document}
              canEdit={canEdit}
              onDelete={() => onChange(null, 'bankDetailsProof', 'proofDocuments')}
              onPreUpload={(value: ProofDocumentFile) => (
                onPreUpload(value, 'bankDetailsProof', 'proofDocuments')
              )}
            />
          </FormField>
        )
      }
      {children}
    </React.Fragment>
  );

  if (!onSubmit) {
    return (
      <React.Fragment>
        {renderFields()}
      </React.Fragment>
    );
  }

  return (
    <Form
      className="bank-details-settings-form"
      key={canEdit.toString()}
      onSubmit={onSubmit}
    >
      {renderFields()}
    </Form>
  );
};

const mapStateToProps = ({ runtimeConfig }): StateProps => ({
  isDocumentProofSectionEnabled: runtimeConfig?.config?.settingsDocumentProofEnabled
});

export default connect(mapStateToProps)(BankDetailsSettingsForm);
